import { Suspense } from 'react';
import { Navigate, useParams } from 'react-router';
import { HasPermission } from '@/components/Permission/HasPermission';
import LoadingCompanyDashboard from '@/components/UI/loading/LoadingCompanyDashboard/index.tsx';
import Unauthorized from '@/components/Unauthorized';
import { ChangeOrderBudgetDetails } from '@/features/ChangeOrder/Details/BudgetDetails';
import { ChangeOrderDeliverableDetails } from '@/features/ChangeOrder/Details/DeliverableDetails';
import { ChangeOrderMilestoneDetails } from '@/features/ChangeOrder/Details/MilestoneDetails';
import { DrawDetails } from '@/features/IDMS';
import DocumentDetails from '@/features/IDMS/components/Draw/DrawDetails/Documents/DocumentDetails';
import InvoiceDetails from '@/features/IDMS/components/Draw/DrawDetails/Invoices/InvoiceDetails';
import { SherpaProjectTemplateDetailsShell, SherpaProjectTemplateTDMImportShell } from '@/features/ProjectTemplates/Shells/Sherpa';
import { NewProjectForm, ProjectAudit, ProjectDetails } from '@/features/projects';
import { ImportProjects } from '@/features/projects/components/ImportProjects';
import { Reports } from '@/features/projects/components/Reports';
import { ProjectReports } from '@/features/projects/components/Reports/ProjectReports';
import { ReportDetails } from '@/features/projects/components/Reports/ReportDetails';
import { ReportSettings } from '@/features/projects/components/Reports/ReportSettings';
import ChangeOrder from '@/pages/Sherpa/ChangeOrder';
import IDMS from '@/pages/Sherpa/IDMS';
import ProjectTemplates from '@/pages/Sherpa/ProjectTemplates';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import type { HasPermissionFn } from '../../../hooks/usePermission';
import PageNotFound from '../../../pages/PageNotFound';
import Projects from '../../../pages/Sherpa/Projects';
import SherpaDashboard from '../../../pages/Sherpa/SherpaDashboard';

export const SherpaRoute = (hasPermission: HasPermissionFn) => {
  const routes = [
    {
      index: true,
      element: <Navigate to={`${hasPermission(['VIEW_SHERPA_DASHBOARD']) ? 'dashboard' : 'projects'}`} />,
    },
    {
      path: 'dashboard',
      element: (
        <HasPermission of='VIEW_SHERPA_DASHBOARD' fallback={<Unauthorized />}>
          <Suspense fallback={<LoadingCompanyDashboard />}>
            <SherpaDashboard />
          </Suspense>
        </HasPermission>
      ),
    },
    {
      path: 'projects',
      children: [
        {
          index: true,
          element: <Projects />,
        },
        {
          path: 'reports',
          children: [
            {
              index: true,
              element: <Reports />,
            },
            {
              path: ':reportId',
              element: <ReportDetails />,
            },
            {
              path: ':reportId/settings',
              element: <ReportSettings />,
            },
          ],
        },
        {
          path: 'new',
          element: (
            <HasPermission of='CREATE_PROJECT' fallback={<Unauthorized />}>
              <NewProjectForm />
            </HasPermission>
          ),
        },
        {
          path: ':projectId/reports',
          children: [
            {
              index: true,
              element: <ProjectReports />,
            },
            {
              path: ':reportId',
              element: <ReportDetails />,
            },
            {
              path: ':reportId/settings',
              element: <ReportSettings />,
            },
          ],
        },
        {
          path: ':projectId/audit',
          element: <ProjectAudit />,
        },
        {
          path: ':projectId/idms',
          children: [
            {
              index: true,
              element: (
                <HasPermission of='LIST_PROJECT_DRAWS' fallback={<Unauthorized />}>
                  <IDMS />
                </HasPermission>
              ),
            },
            {
              path: ':drawId',
              element: (
                <HasPermission of='LIST_PROJECT_DRAWS' fallback={<Unauthorized />}>
                  {' '}
                  <DrawDetails />{' '}
                </HasPermission>
              ),
            },
            {
              path: ':drawId/document',
              element: (
                <HasPermission of='READ_PROJECT_DRAW' fallback={<Unauthorized />}>
                  <DocumentDetails />
                </HasPermission>
              ),
            },
            {
              path: ':drawId/document/:documentId',
              element: (
                <HasPermission of='READ_PROJECT_DRAW' fallback={<Unauthorized />}>
                  <DocumentDetails />
                </HasPermission>
              ),
            },
            {
              path: ':drawId/invoice/:invoiceId',
              element: (
                <HasPermission of='READ_PROJECT_DRAW' fallback={<Unauthorized />}>
                  <InvoiceDetails />
                </HasPermission>
              ),
            },
          ],
        },
        {
          path: ':projectId/change-order',
          children: [
            {
              index: true,
              element: <ChangeOrder />,
            },
            {
              path: 'budget/:changeOrderId',
              element: <ChangeOrderBudgetDetails />,
            },
            {
              path: 'milestones/:changeOrderId',
              element: <ChangeOrderMilestoneDetails />,
            },
            {
              path: 'deliverables/:changeOrderId',
              element: <ChangeOrderDeliverableDetails />,
            },
            {
              path: '*',
              element: <ChangeOrder404 />,
            },
          ],
        },
        {
          path: 'import',
          element: <ImportProjects />,
        },
        {
          path: ':projectId/*',
          element: <ProjectDetails />,
        },
      ],
    },
    {
      path: 'project-templates',
      element: (
        <HasPermission of='LIST_PROJECT_TEMPLATES' fallback={<Unauthorized />}>
          <ProjectTemplates />
        </HasPermission>
      ),
    },
    {
      path: 'project-templates/:templateId',
      element: (
        <HasPermission of='READ_PROJECT_TEMPLATE' fallback={<Unauthorized />}>
          <SherpaProjectTemplateDetailsShell />
        </HasPermission>
      ),
    },
    {
      path: 'project-templates/:templateId/import',
      element: (
        <HasPermission of='UPDATE_PROJECT_TEMPLATE' fallback={<Unauthorized />}>
          <SherpaProjectTemplateTDMImportShell />
        </HasPermission>
      ),
    },
    {
      path: '*',
      element: <PageNotFound />,
    },
  ];

  return routes;
};

function ChangeOrder404() {
  const { projectId } = useParams();

  if (!projectId) {
    return <Navigate to='/app/sherpa/projects' replace />;
  }

  return <Navigate to={buildRoute(AppRoutes.SHERPA_PROJECT_CHANGE_ORDER, { projectId })} />;
}
